import { useLingui } from "@lingui/react/macro";
import { CheckIcon } from "lucide-react";

import { cn } from "@/utils/shadcn-ui";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui-kit/base/dropdown-menu";
import { buttonVariants } from "../../components/ui-kit/button";
import { LocaleFlag } from "../../components/ui-kit/flags/locale-flag";
import {
  localeNames,
  persistLocale,
  switchableLocales,
} from "../../i18n.react";

/** Language picker for the app footer. Persists the choice and reloads so every
 * label — including enum maps resolved through the global `i18n` — reflects the
 * new locale (a live switch would leave those non-subscribed labels stale). */
export function LocaleSwitcher() {
  const { i18n, t } = useLingui();
  const current = i18n.locale;

  const selectLocale = (locale: string) => {
    if (locale === current) return;
    persistLocale(locale);
    // A `?locale=` in the URL outranks the stored choice on startup, so keep it
    // in sync when present; otherwise a plain reload picks up the new locale.
    const url = new URL(window.location.href);
    if (url.searchParams.has("locale")) {
      url.searchParams.set("locale", locale);
      window.location.href = url.toString();
    } else {
      window.location.reload();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t`Change language`}
        title={localeNames[current] ?? current}
        className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
      >
        <LocaleFlag locale={current} />
        {current.toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {switchableLocales.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => selectLocale(locale)}
            className={locale === current ? "text-brand-primary" : ""}
          >
            <CheckIcon
              className={`size-4 ${locale === current ? "" : "invisible"}`}
            />
            <LocaleFlag locale={locale} />
            {localeNames[locale]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
